import {
    Alert,
    Box,
    Button,
    CircularProgress,
    ListSubheader,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { myAppointmentsLink } from '../../../library/constants';
import { useValidatedForm } from '../../../library/formValidation';
import { splitApiError } from '../../../library/useApiFormError';
import { EmptySlotResponse, REQUEST_CONFIRMATION_LEAD_HOURS } from '../../../library/models/appointment';
import { bookableSessions, IPatientPurchase, isUsable } from '../../../library/models/patientPurchase';
import { IService } from '../../../library/models/service';
import { planLabel } from '../../../library/purchaseDisplay';
import { ITherapist } from '../../../library/models/therapist';
import ResourcesIds from '../../../resources/ResourcesIds';
import { AppointmentBookingFormValues, createAppointmentBookingSchema } from './appointmentBookingSchema';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { SlotPicker } from './SlotPicker';
import { useSnackbar } from '../../layout/snackbarProvider';
import { PersonLabel } from '../../userAvatar/UserAvatar';
import {
    confirmRowStyles,
    emptyStateStyles,
    entitlementStyles,
    headerBoxStyles,
    loadingBoxStyles,
    requestHintStyles,
    selectFieldStyles,
    stepBoxStyles,
    stepTitleStyles,
    subtitleStyles,
    titleStyles,
} from './appointmentBookingForm.styles';

/**
 * A patient booking themselves in.
 *
 * The wizard is the same three steps it always was -- a service, a therapist who practises
 * it, then a free slot -- but it is now purchase-aware, which the calendar-backed version
 * could not be: Google Calendar had no idea what anybody had bought, so a patient with
 * nothing left could book anyway and find out at the desk.
 *
 * The entitlement it checks is `bookableSessions`, the remainder *less what is already in
 * the diary*, not the remainder alone. Those are different questions and only one of them
 * stops a patient booking their last session twice.
 *
 * `/services` returns SINGLE entries only unless asked otherwise, which is what keeps
 * packages out of here: a package is a block of sessions sold at the desk, never a thing
 * you book.
 */
export const AppointmentBooking = (): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const navigate: NavigateFunction = useNavigate();

    const schema = useMemo(() => createAppointmentBookingSchema(translate), [translate]);

    const { control, handleSubmit, setValue, formState: { isValid } } =
        useValidatedForm<AppointmentBookingFormValues>(schema, {
            defaultValues: {
                choice: '', therapistId: '', slotStartsAt: '',
            },
        });

    // Every query below keys off one of these, so the whole draft is watched at once.
    const { choice, therapistId, slotStartsAt } =
        useWatch({ control }) as AppointmentBookingFormValues;

    const { data: servicesList, isLoading: isLoadingServices } = useReactQuery<IService[]>(
        services.services.getServices()
    );

    // The patient's own purchases, so the Service step can lead with what is already paid for.
    const { data: purchases } = useReactQuery<IPatientPurchase[]>(
        services.patientPurchases.getPurchases()
    );

    /** Usable purchases with sessions still to book -- what the "Active plans" group offers. */
    const drawableOn: IPatientPurchase[] = (purchases ?? []).filter(
        purchase => isUsable(purchase) && bookableSessions(purchase) > 0
    );

    const chosenPurchase: IPatientPurchase | undefined = choice.startsWith('plan:')
        ? drawableOn.find(purchase => `plan:${purchase.id}` === choice)
        : undefined;

    const chosenService: IService | undefined = choice.startsWith('service:')
        ? (servicesList ?? []).find(option => `service:${option.id}` === choice)
        : undefined;

    /**
     * The catalogue line a plan's sessions come from, when there still is one.
     *
     * The option the purchase records and nothing else. Matching on the name and the
     * duration was the old way and it is gone rather than kept behind this: a package's
     * own line is never in `servicesList` at all -- `GET /services` answers with singles --
     * so for every package the pair was not a fallback but the only branch that ever ran,
     * and it resolved to the *single* line of the same discipline. That is a wrong answer
     * wearing the shape of a right one.
     *
     * Nothing downstream requires this to succeed. It buys one thing -- the colleagues who
     * also practise the discipline -- and a miss shortens that list rather than stopping
     * the booking, because everything else a plan needs is on the plan.
     */
    const catalogueLineFor = (purchase: IPatientPurchase): IService | undefined =>
        (servicesList ?? []).find(option => option.id === purchase.sourceCatalogOptionId);

    const planService: IService | undefined =
        chosenPurchase && catalogueLineFor(chosenPurchase);

    const service: IService | undefined = chosenService ?? planService;

    /**
     * Something has been picked, which is a different question from whether the catalogue
     * could be reached. Every step below hangs off this rather than off `service`: a plan
     * whose line has been retimed or withdrawn is still bookable, and a wizard that answers
     * a choice by drawing nothing at all reads as a broken page rather than as a problem.
     */
    const hasChoice: boolean = !!chosenService || !!chosenPurchase;

    /*
     * The catalogue option to ask about -- a plan's own, not the single session it happens
     * to resemble. `?type=ALL` because a plan is usually a package, and packages are kept
     * out of the default list on purpose.
     */
    const askAbout: number | undefined = chosenService?.id
        ?? chosenPurchase?.sourceCatalogOptionId
        ?? planService?.id;

    const { data: colleagues, isLoading: isLoadingTherapists } = useReactQuery<ITherapist[]>(
        services.therapists.getTherapists(askAbout ?? 0),
        { enabled: !!askAbout }
    );

    /**
     * Who the patient may pick, the plan's own therapist first.
     *
     * The plan names the person who has been giving these sessions, and they are offered
     * from the purchase itself rather than from the catalogue round trip -- so the step
     * stands up even when the price list can no longer be reached. They are dropped when
     * the API says they no longer give sessions here, which also covers a sale recorded by
     * an admin, and de-duplicated against the fetched list so nobody is offered twice.
     */
    const planTherapistId: number | undefined =
        chosenPurchase?.therapistActive ? chosenPurchase.therapistId : undefined;

    const otherTherapists: ITherapist[] = (colleagues ?? []).filter(
        candidate => candidate.id !== planTherapistId
    );

    const hasAnyTherapist: boolean = planTherapistId !== undefined || otherTherapists.length > 0;

    /*
     * The chosen slot, held whole rather than looked back up.
     *
     * `SlotPicker` owns slot discovery now -- it asks for a week at a time, which is what
     * lets it count each day -- so the list is no longer in scope here to resolve a start
     * time against. The form still holds `slotStartsAt`, because that is what the schema
     * gates the Book button on; this carries the `endTime` beside it that the request needs.
     */
    const [slot, setSlot] = useState<EmptySlotResponse | null>(null);

    /** Keeps the validated field and the whole slot in step from one place. */
    const onSelectSlot = (picked: EmptySlotResponse | null): void => {
        setSlot(picked);
        setValue('slotStartsAt', picked?.startTime ?? '', { shouldValidate: true });
    };

    const book = useReactMutation(services.appointments.createAppointmentMutation());

    /** A same-service purchase with nothing left to book, worth telling the patient about. */
    const chosenName: string | undefined = chosenService?.nameRo ?? chosenPurchase?.serviceNameRo;

    const exhausted: IPatientPurchase | undefined = (purchases ?? []).find(
        purchase =>
            isUsable(purchase) &&
            bookableSessions(purchase) === 0 &&
            purchase.serviceNameRo === chosenName
    );

    /**
     * How long to look for. A plan's own length wins over the catalogue's, the same way it
     * does on the therapist's side: it is what was sold, and asking the price list again
     * could only produce a second answer that disagrees.
     */
    const runsFor: number = chosenPurchase?.durationMinutes ?? chosenService?.duration ?? 60;

    const onValid = (): void => {
        if (!hasChoice || !therapistId || !slot) {
            return;
        }

        book.mutate(
            {
                therapistId: Number(therapistId),
                // A plan sends only itself. The API takes the service name from the
                // purchase when one is named, so quoting a catalogue line beside it would
                // be a second answer to a question already answered -- which is how
                // `PatientPurchaseService.recordSessionGiven` has always booked.
                ...(chosenPurchase
                    ? { purchaseId: chosenPurchase.id }
                    : { serviceId: chosenService?.id }),
                startsAt: slot.startTime,
                endsAt: slot.endTime,
            },
            {
                // A request, not a booking: the therapist confirms it, and the patient hears by
                // email. Saying "booked" here is what the old confirmation email got wrong.
                onSuccess: () => {
                    showSnackbar(translate(ResourcesIds.APPOINTMENT_REQUEST_SENT), 'success');
                    navigate(myAppointmentsLink);
                },
                // The API's own sentence when it sent one -- above all "needs at least four
                // hours' notice", for a slot chosen a minute before it went stale -- since a
                // generic failure there leaves the patient retrying something that cannot work.
                onError: (error: unknown) => showSnackbar(
                    splitApiError(error, [], translate, ResourcesIds.APPOINTMENT_BOOK_FAILED).formError
                        ?? translate(ResourcesIds.APPOINTMENT_BOOK_FAILED), 'error'),
            }
        );
    };

    return (
        <>
            <Box sx={headerBoxStyles}>
                <Typography variant="h4" component="h1" gutterBottom sx={titleStyles}>
                    {translate(ResourcesIds.BOOK_APPOINTMENT)}
                </Typography>
                <Typography sx={subtitleStyles}>
                    {translate(ResourcesIds.BOOK_APPOINTMENT_SUBTITLE)}
                </Typography>
            </Box>

            {/* Said before the slot is picked, not after: finding out at the confirm step
                that there is nothing left to book is the worst moment to be told. */}
            {chosenPurchase && (
                <Alert severity="info" sx={entitlementStyles}>
                    {translate(ResourcesIds.BOOK_APPOINTMENT_ENTITLEMENT, {
                        remaining: chosenPurchase.remainingSessions,
                        booked: chosenPurchase.scheduledSessions,
                    })}
                </Alert>
            )}

            {chosenService && exhausted && (
                <Alert severity="warning" sx={entitlementStyles}>
                    {translate(ResourcesIds.BOOK_APPOINTMENT_NOTHING_LEFT)}
                </Alert>
            )}

            <Box sx={stepBoxStyles}>
                <Typography sx={stepTitleStyles}>
                    {translate(ResourcesIds.BOOK_APPOINTMENT_STEP_SERVICE)}
                </Typography>

                {isLoadingServices ? (
                    <Box sx={loadingBoxStyles}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    // One pick answers what a service and a purchase used to answer separately:
                    // a plan is a service too, just one that has already been paid for.
                    // Grouped only once there is a plan to group against -- with none, this is
                    // the flat list it always was. Same pattern as the therapist-side dialog.
                    <TextField
                        select
                        fullWidth
                        size="small"
                        sx={selectFieldStyles}
                        label={translate(ResourcesIds.MY_CALENDAR_ADD_SERVICE)}
                        value={choice}
                        onChange={event => {
                            setValue('choice', event.target.value, { shouldValidate: true });
                            setValue('therapistId', '', { shouldValidate: true });
                            setValue('slotStartsAt', '', { shouldValidate: true });
                        }}
                    >
                        {drawableOn.length > 0
                            ? [
                                <ListSubheader key="plans-header">
                                    {translate(ResourcesIds.MY_CALENDAR_ADD_PLANS)}
                                </ListSubheader>,
                                ...drawableOn.map(purchase => (
                                    <MenuItem key={`plan:${purchase.id}`} value={`plan:${purchase.id}`}>
                                        {translate(ResourcesIds.APPOINTMENT_PURCHASE_OPTION, {
                                            service: planLabel(purchase, translate, i18n.language),
                                            remaining: bookableSessions(purchase),
                                        })}
                                    </MenuItem>
                                )),
                                <ListSubheader key="services-header">
                                    {translate(ResourcesIds.MY_CALENDAR_ADD_NO_PURCHASE)}
                                </ListSubheader>,
                            ]
                            : []}
                        {(servicesList ?? []).map(option => (
                            <MenuItem key={`service:${option.id}`} value={`service:${option.id}`}>
                                {`${option.nameRo} · ${option.durationLabelRo}`}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </Box>

            {hasChoice && (
                <Box sx={stepBoxStyles}>
                    <Typography sx={stepTitleStyles}>
                        {translate(ResourcesIds.BOOK_APPOINTMENT_STEP_THERAPIST)}
                    </Typography>

                    {isLoadingTherapists ? (
                        <Box sx={loadingBoxStyles}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : !hasAnyTherapist ? (
                        <Typography sx={emptyStateStyles}>
                            {translate(ResourcesIds.BOOK_APPOINTMENT_NO_THERAPISTS)}
                        </Typography>
                    ) : (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            sx={selectFieldStyles}
                            label={translate(ResourcesIds.ADD_PURCHASE_THERAPIST_LABEL)}
                            value={therapistId}
                            onChange={event => {
                                setValue('therapistId', event.target.value, { shouldValidate: true });
                                setValue('slotStartsAt', '', { shouldValidate: true });
                            }}
                        >
                            {/* Headings only when there are two groups to tell apart, the
                                same rule the step above follows: one of them alone is a
                                label on a list of one, which says nothing. */}
                            {chosenPurchase && planTherapistId !== undefined
                                ? [
                                    ...(otherTherapists.length > 0
                                        ? [
                                            <ListSubheader key="plan-therapist-header">
                                                {translate(
                                                    ResourcesIds.BOOK_APPOINTMENT_YOUR_THERAPIST
                                                )}
                                            </ListSubheader>,
                                        ]
                                        : []),
                                    <MenuItem key={planTherapistId} value={String(planTherapistId)}>
                                        <PersonLabel
                                            person={{
                                                name: chosenPurchase.therapistName,
                                                pictureUrl: chosenPurchase.therapistPictureUrl,
                                            }}
                                        />
                                    </MenuItem>,
                                    ...(otherTherapists.length > 0
                                        ? [
                                            <ListSubheader key="other-therapists-header">
                                                {translate(
                                                    ResourcesIds.BOOK_APPOINTMENT_OTHER_THERAPISTS
                                                )}
                                            </ListSubheader>,
                                        ]
                                        : []),
                                ]
                                : []}
                            {otherTherapists.map(option => (
                                <MenuItem key={option.id} value={String(option.id)}>
                                    <PersonLabel person={option} />
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Box>
            )}

            {hasChoice && therapistId && (
                <Box sx={stepBoxStyles}>
                    <Typography sx={stepTitleStyles}>
                        {translate(ResourcesIds.BOOK_APPOINTMENT_STEP_SLOT)}
                    </Typography>

                    <SlotPicker
                        therapistId={Number(therapistId)}
                        durationMinutes={runsFor}
                        serviceId={askAbout}
                        selected={slot}
                        onSelect={onSelectSlot}
                    />

                    {/* Said before the press rather than after it: what the patient is
                        about to send is a request, and the hour is theirs only once the
                        therapist agrees. */}
                    <Typography sx={requestHintStyles}>
                        {translate(ResourcesIds.BOOK_APPOINTMENT_REQUEST_HINT, {
                            hours: REQUEST_CONFIRMATION_LEAD_HOURS,
                        })}
                    </Typography>

                    <Box sx={confirmRowStyles}>
                        <Button
                            variant="contained"
                            disabled={!isValid || !slot || book.isPending}
                            onClick={handleSubmit(onValid)}
                        >
                            {translate(ResourcesIds.BOOK_APPOINTMENT_CONFIRM)}
                        </Button>
                    </Box>
                </Box>
            )}
        </>
    );
};
